/**
 * Facts the shop pages read from the real catalogue, so filters, category tiles and menus only offer what the
 * store actually stocks (fixed lists like "Serum" or "Body Wash" led to empty pages: the admin's types are
 * "Face Serum", "Shampoo & Conditioner", "Hair Treatment"…).
 */

/** A product's type as entered in the admin, compared without case or extra spaces */
export const typeKey = (p) => String(p?.type || p?.productType || '').trim().toLowerCase();

/** The price the shopper sees: the market price abroad, the store price at home */
export const priceOf = (p) => Number(p?.marketPrice ?? p?.price) || 0;

const tagText = (p) => (Array.isArray(p?.tags) ? p.tags.join(' ') : String(p?.tags || ''));

/** Everything a search box or a skin-concern tile may match: title, brand, type, tags and description */
export const searchText = (p) =>
  `${p?.title || ''} ${p?.vendor || ''} ${p?.type || p?.productType || ''} ${tagText(p)} ${p?.description || ''}`.toLowerCase();

/** The store's product types, biggest first: [{ key, label, count, image }] (image = a real product photo) */
export function typeGroups(products) {
  const groups = new Map();
  (products || []).forEach((p) => {
    const key = typeKey(p);
    if (!key) return;
    const g = groups.get(key) || { key, label: String(p.type || p.productType).trim(), count: 0, image: null };
    g.count += 1;
    if (!g.image) g.image = p.images?.[0] || p.primaryImageUrl || null;
    groups.set(key, g);
  });
  return [...groups.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

const singular = (w) => w.replace(/(es|s)$/, '');

/**
 * The types a link like /shop?type=… means: the exact type ("Face Serum", "face-serum") if the store has it,
 * otherwise every type containing that word (?type=Serum → Face Serum + Hair Serum). Older links and ads keep working.
 */
export function typesForParam(raw, groups) {
  if (!raw) return [];
  const wanted = String(raw).replace(/[-+_]/g, ' ').trim().toLowerCase();
  if (!wanted) return [];
  const exact = groups.find((g) => g.key === wanted || g.key.replace(/[^a-z0-9]+/g, ' ').trim() === wanted);
  if (exact) return [exact.key];
  const word = singular(wanted);
  return groups.filter((g) => g.key.includes(word)).map((g) => g.key);
}

/** True when at least one product has skin types filled in (the Skin Type filter is useless otherwise) */
export const hasSkinTypes = (products) => (products || []).some((p) => Array.isArray(p.skinType) && p.skinType.length > 0);
